"use client";

import { Fragment, useEffect, useState } from "react";
import NextLink from "next/link";
import { Box, Button, Heading, Link, Separator, Stack, Text } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { website_name } from "@/app/constants";


type FooterSection = {
  title: string;
  links: { label: string; href: string }[];
};

const footerSections: FooterSection[] = [
  {
    title: "Important Links",
    links: [
      { label: "Home", href: "/" },
      { label: "About", href: "/about" },
      { label: "Courses", href: "/courses" },
      { label: "Why Learn Bengali", href: "/why-learn-bengali" },
      { label: "Blog", href: "/blog" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Contact", href: "/contact" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Use", href: "/terms" },
    ],
  },
];

type GoogleFundingChoices = {
  // Google's Privacy & messaging API accepts plain callbacks or keyed objects
  // such as { CONSENT_API_READY: () => void }.
  callbackQueue: Array<(() => void) | Record<string, () => void>>;
  showRevocationMessage?: () => void;
};

declare global {
  interface Window {
    googlefc?: GoogleFundingChoices;
  }
}

const handleCookieSettings = () => {
  window.googlefc?.showRevocationMessage?.();
};

export function Footer() {
  const pathname = usePathname();
  // "Cookie settings" only appears once Google's consent API has loaded. Until
  // then (for example before the site is approved, or when no message is
  // published) there is nothing for the button to open.
  const [cookieSettingsAvailable, setCookieSettingsAvailable] = useState(false);

  useEffect(() => {
    const fc = (window.googlefc = window.googlefc || { callbackQueue: [] });
    fc.callbackQueue = fc.callbackQueue || [];

    // Google's API may already be loaded by the time this mounts.
    if (typeof fc.showRevocationMessage === "function") {
      const timer = window.setTimeout(() => setCookieSettingsAvailable(true), 0);
      return () => window.clearTimeout(timer);
    }

    fc.callbackQueue.push({
      CONSENT_API_READY: () => setCookieSettingsAvailable(true),
    });
  }, []);


  if (pathname === "/SignIn" || pathname.startsWith("/SignUp")) {
    return null;
  }
  return (
    <Box
      as="footer"
      display="flex"
      justifyContent="center"
      position="relative"
      zIndex={30}
      pointerEvents="auto"
      borderTopWidth="1px"      
    >
      <Box flex="1" maxW="960px">
        <Stack
          gap={8}
          px={{ base: 5, md: 8 }}
          py={{ base: 10, md: 12 }}
        >
          <Stack
            direction={{ base: "column", sm: "row" }}
            align={{ base: "center", sm: "stretch" }}
            gap={{ base: 8, sm: 6 }}
            w="full"
            textAlign={{ base: "center", sm: "left" }}
          >
            {footerSections.map((section, index) => (
              <Fragment key={section.title}>
                {index > 0 && (
                  <Separator
                    orientation={{ base: "horizontal", sm: "vertical" }}
                    w={{ base: "full", sm: "auto" }}
                  />
                )}
                <Stack gap="sm" alignItems={{ base: "center", sm: "flex-start" }}>
                  <Heading
                    as="h3"
                    fontSize="sm"
                    fontWeight="bold"
                    fontStyle="italic"
                    textDecoration="underline"
                    fontFamily="Georgia, 'Times New Roman', serif"
                    color="red.600"
                    _dark={{ color: "red.400" }}
                  >
                    {section.title}
                  </Heading>
                  <Stack gap="xs" alignItems={{ base: "center", sm: "flex-start" }}>
                    {section.links.map((link) => {
                      const active = pathname === link.href;
                      return (
                        <Link
                          key={link.label}
                          asChild
                          display="inline-flex"
                          alignItems="center"
                          py={1.5}
                          fontSize="13px"
                          fontWeight={active ? "bold" : "none"}
                          color={active ? "red.600" : undefined}
                          _dark={active ? { color: "red.400" } : undefined}
                          transition="color 0.2s ease"
                          _hover={{
                            color: "red.600",
                            _dark: { color: "red.400" },
                          }}
                        >
                          <NextLink href={link.href}>{link.label}</NextLink>
                        </Link>
                      );
                    })}
                    {section.title === "Legal" && cookieSettingsAvailable ? (
                      <Button
                        variant="plain"
                        h="auto"
                        px={0}
                        py={1.5}
                        fontSize="13px"
                        fontWeight="normal"
                        transition="color 0.2s ease"
                        _hover={{
                          color: "red.600",
                          _dark: { color: "red.400" },
                        }}
                        onClick={handleCookieSettings}
                      >
                        Cookie settings
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Fragment>
            ))}
          </Stack>

          <Separator />

          <Text fontSize="xs" fontWeight="normal" textAlign="center">
            Copyright @2026 {website_name}. All rights reserved.
          </Text>
        </Stack>
      </Box>
    </Box>
  );
}
